import {
  Badge,
  Button,
  Checkbox,
  Col,
  Divider,
  Image,
  Popover,
  Row,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
} from "antd"
import React, { useEffect, useState } from "react"
import PageHeader from "../../components/Elements/PageHeader"
import { ShoppingCartOutlined, FilterOutlined, FilterFilled } from "@ant-design/icons"
import moment from "moment"
import { ASAPOrder, OrderAndPrefix } from "../../components/Orders/Helpers/OrderAndPrefix"
import "../../styles/OrderTable.css"
import { collection, doc, onSnapshot, query, where } from "firebase/firestore"
import { db } from "../../firebase/config"
import OrderTicketButton from "../../components/Tickets/OrderTicketDrawer"
import WinderCompleteOrder from "./WinderCompleteOrder"
import ActiveOrderBar from "../../components/Machines/ActiveOrderBar"
import MakeCurrentlyPrinting from "../../components/Machines/ActivePrinting/MakeCurrentlyPrinting"
import "./WinderOrders.css"

const { Text } = Typography

export default function WinderOrders() {
  const [loading, setLoading] = useState(false)
  const [winderData, setWinderData] = useState([])
  const [winderSequence, setWinderSequence] = useState(winderData.map((item) => item.id))
  const [visibleColumns, setVisibleColumns] = useState({
    orderNumber: true,
    priority: true,
    client: true,
    product: true,
    manufacturer: true,
    machine: true,
    width: true,
    height: true,
    amount: true,
    created: false,
    dueDate: true,
    technical: true,
    action: true,
  })
  const [filterActive, setFilterActive] = useState(false)
  const activeOrder = winderData.find((r) => r.currentlyInUseForWinder)

  //gets array of the sequence
  useEffect(() => {
    const sequenceDocRef = doc(db, "sequence", "documentForWinderSequence")
    const unsubscribe = onSnapshot(sequenceDocRef, (doc) => {
      if (doc.exists()) {
        const sequenceData = doc.data()
        setWinderSequence(sequenceData.winderSequence)
        console.log("sequence")
      }
    })
    return () => unsubscribe()
  }, [])

  // //fetches the documents, then uses the sequence array to sort in correct order
  useEffect(() => {
    const ref = collection(db, "orders")
    const q = query(ref, where("orderStatus", "==", 5))

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const results = snapshot.docs.map((doc) => ({
        ...doc.data(),
        key: doc.id,
        id: doc.id,
      }))

      // Sort by existing sequence, new items go to the end
      const sortedResults = results.sort((a, b) => {
        const indexA = winderSequence.indexOf(a.id)
        const indexB = winderSequence.indexOf(b.id)

        if (indexA === -1 && indexB === -1) return 0
        if (indexA === -1) return 1
        if (indexB === -1) return -1
        return indexA - indexB
      })

      setWinderData(sortedResults)
      setLoading(false)
    })

    return () => unsubscribe()
  }, [winderSequence])

  const handleVisibilityChange = (key, checked) => {
    setVisibleColumns((prev) => {
      const updated = { ...prev, [key]: checked }
      const isAnyHidden = Object.values(updated).some((v) => !v)
      setFilterActive(isAnyHidden)
      return updated
    })
  }

  const columnVisibilityControls = (
    <div style={{ margin: -8, background: "white", borderRadius: "5px" }}>
      <Space direction="vertical" style={{ padding: 15 }}>
        {[
          { key: "orderNumber", label: "Order Number" },
          { key: "priority", label: "Priority" },
          { key: "client", label: "Client" },
          { key: "product", label: "Product" },
          { key: "manufacturer", label: "Manufacturer" },
          { key: "machine", label: "Machine" },
          { key: "width", label: "Width" },
          { key: "height", label: "Height" },
          { key: "amount", label: "Req. Amount" },
          { key: "created", label: "Created" },
          { key: "dueDate", label: "Due Date" },
          { key: "technical", label: "Technical" },
          { key: "action", label: "Action" },
        ].map((col) => (
          <Checkbox
            key={col.key}
            checked={visibleColumns[col.key]}
            onChange={(e) => handleVisibilityChange(col.key, e.target.checked)}>
            {col.label}
          </Checkbox>
        ))}
      </Space>
    </div>
  )

  //default columns for table
  const defaultColumns = [
    {
      title: "Nr.",
      dataIndex: "orderNumber",
      key: "orderNumber",
      align: "left",
      sorter: (a, b) => a.orderNumber - b.orderNumber,
      render: (value, record) => (
        <Space>
          {ASAPOrder(record)}
          {OrderAndPrefix(record)}
        </Space>
      ),
    },
    {
      title: "Priority",
      width: "5%",
      dataIndex: "id",
      key: "priority",
      align: "center",
      render: (id) => <Badge color="blue" count={winderSequence.indexOf(id) + 1} />,
    },
    {
      title: "Client",
      dataIndex: ["client", "client"],
      key: "client",
      align: "left",
    },
    {
      title: "Product",
      dataIndex: ["selectedProduct", "product", "productName"],
      key: "product",
      align: "left",
    },
    {
      title: "Manufacturer",
      dataIndex: ["manufacturer", "company"],
      key: "manufacturer",
      align: "left",
      ellipsis: true,
      render: (text) => (
        <Tooltip title={text}>
          <Text ellipsis>{text || "—"}</Text>
        </Tooltip>
      ),
    },
    {
      title: "Machine",
      dataIndex: ["selectedProduct", "machine"],
      key: "machine",
      width: 100,
      align: "center",
    },
    {
      title: "W",
      dataIndex: ["selectedProduct", "labelWidth"],
      key: "width",
      align: "center",
    },
    {
      title: "H",
      dataIndex: ["selectedProduct", "labelHeight"],
      key: "height",
      align: "center",
    },
    {
      title: "Req.",
      dataIndex: "labelCount",
      key: "amount",
      align: "center",
    },
    {
      title: "Created",
      dataIndex: "created",
      key: "created",
      align: "center",
      sorter: (a, b) => a.date.created.seconds - b.date.created.seconds,
      render: (value) => (
        <>
          <Text>{moment.unix(value.seconds).format("DD/MM/YYYY")}</Text>
        </>
      ),
    },
    {
      title: "Due D.",
      dataIndex: "orderDueDate",
      key: "dueDate",
      align: "center",
      sorter: (a, b) => a.orderDueDate.seconds - b.orderDueDate.seconds,
      render: (value, record) => {
        const dueDate = moment.unix(value.seconds)
        const isOverdue = dueDate.isBefore(moment(), "day")
        if (isOverdue) {
          return <Tag color="red"> {dueDate.format("DD.MM.YYYY")}</Tag>
        } else {
          return <Tag color="blue">{dueDate.format("DD.MM.YYYY")}</Tag>
        }
      },
    },
    {
      title: "File",
      dataIndex: "technicalPage",
      key: "technical",
      align: "center",
      width: "5px",
      render: (value) => <Image src={value} width={25} height={25} />,
    },
    {
      title: "Action",
      key: "action",
      align: "center",
      render: (value, record) => (
        <Space size="middle">
          <WinderCompleteOrder
            data={record}
            winderSequence={winderSequence}
            setWinderSequence={setWinderSequence}
            disabled={!record.currentlyInUseForWinder}
          />
          {/* <OrderTicketButton order={record} /> */}

          <MakeCurrentlyPrinting
            machine="Winder"
            orderId={record.id}
            currentlyInUse={record.currentlyInUseForWinder}
            startedAt={record.jobStartedAtWinder}
            exclusive={true}
          />
        </Space>
      ),
    },
  ]

  const filteredColumns = defaultColumns.filter((col) => visibleColumns[col.key])

  return (
    <div style={{ padding: 15 }}>
      <Row gutter={[15, 15]}>
        <Col span={24}>
          <PageHeader text="Winder Active Orders" icon={<ShoppingCartOutlined />} />
        </Col>
        <Col span={24}>
          <ActiveOrderBar
            order={activeOrder}
            machine="Winder"
            showProjectedMeters={false} // usually not relevant on Winder
          />
        </Col>
        <Col span={24}>
          <Space split={<Divider type="vertical" />} style={{ marginBottom: 16 }}>
            <Popover
              color="#597ef7"
              placement="bottomLeft"
              trigger="click"
              content={columnVisibilityControls}>
              <Button
                type={filterActive ? "primary" : "dashed"}
                icon={filterActive ? <FilterFilled /> : <FilterOutlined />}>
                <Space>
                  Filter Columns
                  {filterActive ? (
                    <Text style={{ color: "lightgray" }} italic>
                      (active)
                    </Text>
                  ) : (
                    ""
                  )}
                </Space>
              </Button>
            </Popover>
          </Space>
          <Table
            size="small"
            dataSource={winderData}
            loading={loading}
            columns={filteredColumns}
            rowKey={(record) => record.id}
            rowClassName={(record) => {
              const classes = []
              if (record.priorityOrder) classes.push("red-background")
              if (record.currentlyInUseForWinder) classes.push("active-row")
              return classes.join(" ")
            }}
            pagination={{ pageSize: 100 }}
            scroll={{ x: "max-content", y: "calc(100vh - 400px)" }}
            bordered
          />
        </Col>
      </Row>
    </div>
  )
}
